import { Injectable } from '@angular/core';

export interface ConfiguracaoCameraFacial {
  zoomCamera?: number;
  areaLeituraCameraPercentual?: number;
}

export interface FonteAnaliseCameraFacial {
  fonte: HTMLVideoElement | HTMLCanvasElement;
  deslocamentoX: number;
  deslocamentoY: number;
  escalaX: number;
  escalaY: number;
  larguraVideo: number;
  alturaVideo: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoCameraFacial {
  onTrilhaEncerrada?: () => void;
  onTrilhaLiberada?: () => void;

  private stream?: MediaStream;
  private removerObservadores?: () => void;
  private configuracao: ConfiguracaoCameraFacial = {
    zoomCamera: 1,
    areaLeituraCameraPercentual: 100
  };
  private canvasAnalise = document.createElement('canvas');
  private canvasLuminosidade = document.createElement('canvas');

  configurar(configuracao?: ConfiguracaoCameraFacial) {
    this.configuracao = {
      zoomCamera: this.normalizarZoom(configuracao?.zoomCamera),
      areaLeituraCameraPercentual: this.normalizarAreaLeitura(configuracao?.areaLeituraCameraPercentual)
    };
    void this.aplicarZoomOptico();
  }

  async abrir(): Promise<MediaStream> {
    const cameraMobile = this.ehCameraMobile();
    this.fechar();
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: cameraMobile ? 720 : 1280 },
        height: { ideal: cameraMobile ? 1280 : 720 },
        facingMode: { ideal: 'user' }
      },
      audio: false
    });
    await this.aplicarZoomOptico();
    this.observarTrilha();
    return this.stream;
  }

  conectarVideo() {
    const video = this.videoAtual();
    if (video && this.stream) {
      video.srcObject = this.stream;
      void video.play().catch(() => undefined);
    }
  }

  trilhaViva() {
    const track = this.stream?.getVideoTracks()[0];
    return Boolean(this.stream?.active && track && track.readyState === 'live');
  }

  videoPausado() {
    const video = this.videoAtual();
    return Boolean(video && (video.paused || video.ended));
  }

  videoSemQuadro() {
    const video = this.videoAtual();
    return !video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA;
  }

  quadroEscuro() {
    const video = this.videoAtual();
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !video.videoWidth) {
      return true;
    }
    const canvas = this.canvasLuminosidade;
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return false;
    }
    context.drawImage(video, 0, 0, 32, 32);
    const pixels = context.getImageData(0, 0, 32, 32).data;
    let soma = 0;
    for (let indice = 0; indice < pixels.length; indice += 4) {
      soma += (pixels[indice] + pixels[indice + 1] + pixels[indice + 2]) / 3;
    }
    return soma / (pixels.length / 4) < 14;
  }

  garantirReproducao() {
    const video = this.videoAtual();
    if (!video || !this.stream) {
      return;
    }
    if (video.srcObject !== this.stream) {
      video.srcObject = this.stream;
    }
    void video.play().catch(() => undefined);
  }

  capturarImagemBase64() {
    const video = this.videoAtual();
    const canvas = document.querySelector<HTMLCanvasElement>('#cameraCanvas');
    if (!video || !canvas) {
      return '';
    }

    this.desenharFonteAmpliada(video, canvas);
    return canvas.toDataURL('image/jpeg', 0.86);
  }

  prepararFonteAnalise(video: HTMLVideoElement): FonteAnaliseCameraFacial {
    const larguraVideo = video.videoWidth || video.clientWidth || 640;
    const alturaVideo = video.videoHeight || video.clientHeight || 480;
    const area = this.normalizarAreaLeitura(this.configuracao.areaLeituraCameraPercentual);

    if (area >= 99) {
      return {
        fonte: video,
        deslocamentoX: 0,
        deslocamentoY: 0,
        escalaX: 1,
        escalaY: 1,
        larguraVideo,
        alturaVideo
      };
    }

    const canvas = this.canvasAnalise;
    const recorte = this.calcularRecorte(larguraVideo, alturaVideo, area);
    canvas.width = larguraVideo;
    canvas.height = alturaVideo;
    const context = canvas.getContext('2d');
    context?.drawImage(
      video,
      recorte.x,
      recorte.y,
      recorte.largura,
      recorte.altura,
      0,
      0,
      larguraVideo,
      alturaVideo
    );

    return {
      fonte: canvas,
      deslocamentoX: recorte.x,
      deslocamentoY: recorte.y,
      escalaX: recorte.largura / larguraVideo,
      escalaY: recorte.altura / alturaVideo,
      larguraVideo,
      alturaVideo
    };
  }

  fatorZoomVisual() {
    const area = this.normalizarAreaLeitura(this.configuracao.areaLeituraCameraPercentual);
    return Math.max(1, Math.min(2.25, 100 / area));
  }

  private desenharFonteAmpliada(video: HTMLVideoElement, canvas: HTMLCanvasElement) {
    const larguraVideo = video.videoWidth || 640;
    const alturaVideo = video.videoHeight || 480;
    const area = this.normalizarAreaLeitura(this.configuracao.areaLeituraCameraPercentual);
    const recorte = this.calcularRecorte(larguraVideo, alturaVideo, area);
    canvas.width = larguraVideo;
    canvas.height = alturaVideo;
    const context = canvas.getContext('2d');
    context?.drawImage(video, recorte.x, recorte.y, recorte.largura, recorte.altura, 0, 0, canvas.width, canvas.height);
  }

  videoAtual() {
    return document.querySelector<HTMLVideoElement>('#cameraVideo');
  }

  fechar() {
    this.removerObservadores?.();
    this.removerObservadores = undefined;
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = undefined;
  }

  private observarTrilha() {
    this.removerObservadores?.();
    const track = this.stream?.getVideoTracks()[0];
    if (!track || !this.stream) {
      return;
    }
    const aoEncerrar = () => this.onTrilhaEncerrada?.();
    const aoLiberar = () => {
      this.garantirReproducao();
      this.onTrilhaLiberada?.();
    };
    track.addEventListener('ended', aoEncerrar);
    track.addEventListener('unmute', aoLiberar);
    this.stream.addEventListener('inactive', aoEncerrar);
    this.removerObservadores = () => {
      track.removeEventListener('ended', aoEncerrar);
      track.removeEventListener('unmute', aoLiberar);
      this.stream?.removeEventListener('inactive', aoEncerrar);
    };
  }

  ehCameraMobile(video?: HTMLVideoElement) {
    const telaEstreita = window.matchMedia('(max-width: 900px)').matches;
    const toque = window.matchMedia('(pointer: coarse)').matches;
    const videoVertical = video ? video.clientHeight > video.clientWidth : false;
    return telaEstreita || toque || videoVertical;
  }

  private async aplicarZoomOptico() {
    const track = this.stream?.getVideoTracks()[0];
    if (!track?.getCapabilities || !track.applyConstraints) {
      return;
    }

    const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
      zoom?: { min?: number; max?: number; step?: number };
    };
    if (capabilities.zoom?.min == null || capabilities.zoom?.max == null) {
      return;
    }

    const zoom = Math.max(capabilities.zoom.min, Math.min(capabilities.zoom.max, this.normalizarZoom(this.configuracao.zoomCamera)));
    await track.applyConstraints({ advanced: [{ zoom } as MediaTrackConstraintSet] }).catch(() => undefined);
  }

  private calcularRecorte(larguraVideo: number, alturaVideo: number, area: number) {
    const fator = area / 100;
    const largura = larguraVideo * fator;
    const altura = alturaVideo * fator;
    return {
      x: (larguraVideo - largura) / 2,
      y: (alturaVideo - altura) / 2,
      largura,
      altura
    };
  }

  private normalizarZoom(zoomCamera?: number) {
    if (!Number.isFinite(zoomCamera)) {
      return 1;
    }
    return Math.max(1, Math.min(3, Number(zoomCamera)));
  }

  private normalizarAreaLeitura(areaLeituraCameraPercentual?: number) {
    if (!Number.isFinite(areaLeituraCameraPercentual)) {
      return 100;
    }
    return Math.max(45, Math.min(100, Number(areaLeituraCameraPercentual)));
  }
}
